import { pickMainSasFromCatalogRow, sortCatalogByDisplayName } from '@buxus/shared'
import { BUXUS_GET_ALL_OPERATORS_WITH_VERSION_URL } from '../abp/config'
import { httpJson } from '../abp/httpClient'
import { reportSasFetchFailure } from '../errors/webFailureReporting'
import { rewriteAzureBlobUrlToContentDomain } from './azureBlobUrl'
import type { GetAllOperatorsWithVersionResponseDto, OperatorWithVersionDto } from './types'

export { sortCatalogByDisplayName }

function normalizeOperatorsResponse(
  raw: GetAllOperatorsWithVersionResponseDto,
): { success: boolean; operators: OperatorWithVersionDto[] } {
  const success = raw.success ?? raw.Success ?? false
  const rows = raw.operators ?? raw.Operators ?? []
  const operators = rows.map((op) => {
    const tenancyName =
      op.tenancyName ??
      (op as OperatorWithVersionDto & { TenancyName?: string | null }).TenancyName ??
      null
    return tenancyName ? { ...op, tenancyName } : op
  })
  return { success, operators }
}

export function pickOperatorSas(op: OperatorWithVersionDto): string | null {
  return pickMainSasFromCatalogRow(op as unknown as Record<string, unknown>)
}

export async function fetchOperatorsWithVersion(): Promise<OperatorWithVersionDto[]> {
  const raw = await httpJson<GetAllOperatorsWithVersionResponseDto>(
    BUXUS_GET_ALL_OPERATORS_WITH_VERSION_URL,
    { method: 'GET', auth: true },
  )
  const { success, operators } = normalizeOperatorsResponse(raw)
  if (!success) {
    throw new Error('get-all-operators-with-version returned success=false')
  }
  return operators
}

/**
 * Fetch operator JSON from an Azure SAS URL (do not send Bearer token).
 */
function sasFetchHeaders(sasUrl: string): Record<string, string> {
  const headers: Record<string, string> = {}
  if (sasUrl.includes('.ngrok-free.app')) {
    headers['ngrok-skip-browser-warning'] = 'true'
  }
  return headers
}

export async function fetchOperatorJsonFromSas(sasUrl: string): Promise<unknown> {
  const url = rewriteAzureBlobUrlToContentDomain(sasUrl)
  const headers: Record<string, string> = { Accept: 'application/json', ...sasFetchHeaders(url) }
  try {
    const res = await fetch(url, { method: 'GET', headers })
    if (!res.ok) {
      const text = await res.text().catch(() => '')
      const err = new Error(`SAS fetch failed ${res.status}: ${text.slice(0, 200)}`)
      reportSasFetchFailure(err, { kind: 'json', sasUrl: url, httpStatus: res.status })
      throw err
    }
    return (await res.json()) as unknown
  } catch (error) {
    if (error instanceof Error && error.message.startsWith('SAS fetch failed')) {
      throw error
    }
    reportSasFetchFailure(error, { kind: 'json', sasUrl: url })
    throw error
  }
}

export async function fetchSasBlobDrain(sasUrl: string): Promise<void> {
  await fetchSasBlob(sasUrl)
}

/** Fetch full blob from a catalog SAS URL (attachments / manifest files). */
export async function fetchSasBlob(
  sasUrl: string,
  options?: { ignored?: boolean },
): Promise<Blob> {
  const url = rewriteAzureBlobUrlToContentDomain(sasUrl)
  const ignored = options?.ignored === true
  try {
    const res = await fetch(url, { method: 'GET', headers: sasFetchHeaders(url) })
    if (!res.ok) {
      const text = await res.text().catch(() => '')
      const err = new Error(`SAS fetch failed ${res.status}: ${text.slice(0, 200)}`)
      reportSasFetchFailure(err, {
        kind: 'blob',
        sasUrl: url,
        httpStatus: res.status,
        ...(ignored ? { ignored: true } : {}),
      })
      throw err
    }
    return res.blob()
  } catch (error) {
    if (error instanceof Error && error.message.startsWith('SAS fetch failed')) {
      throw error
    }
    reportSasFetchFailure(error, {
      kind: 'blob',
      sasUrl: url,
      ...(ignored ? { ignored: true } : {}),
    })
    throw error
  }
}
